import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked, cached } from '@glimmer/tracking';
import { setUserProperty, clearUser } from 'tio-common/utils/datadog';
import ENV from 'tio-employee/config/environment';
import type SessionService from 'tio-employee/services/session';
import type ZendeskService from 'tio-employee/services/zendesk';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import UserModel from 'tio-common/models/user';
import Company from 'tio-common/models/company';
import type RoleViewModel from 'tio-common/models/role-view';
import type RouterService from 'tio-employee/services/router';
import type PreRegisterService from 'tio-employee/services/pre-register';
import type EmployeeModel from 'tio-common/models/employee';
import type AgreementModel from 'tio-common/models/agreement';
import { trackedArray } from '@ember/reactive/collections';
import type PersonModel from 'tio-common/models/person';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';
import { getSubdomain, getPartnerLogoUrl, isLocalhost } from 'tio-common/utils/hostname';

const meIncludes = [
  'person.employees.company',
  'person.dependent.employee.company.company-setting',
  'user-roles.relationships.jurisdiction.company.company-setting.company-lpn-settings.lpn-page',
  'logins',
  'usage-agreement',
  'privacy-agreement',
  'custom-register-agreements.condition',
  'email-addresses',
  'employees.person',
  'employees.jurisdictive-relationships',
  'employees.company.pslf-groups',
  'employees.company.company-setting',
  'employees.scholarships',
  'employees.plan',
  'employees.match-participant.match-plan',
  'employees.tas-participant',
  'employees.tas-eligibilities',
  'employees.employee-addition',
  'employees.pslf-profile',
];

/**
 * Contains in-memory data and actions relevant to a user's session (as opposed
 * to the persisted data kept in the `session` service).
 *
 * @memberof services
 */
class SessionContextService extends Service {
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare store: StoreService;
  @service declare zendesk: ZendeskService;
  @service declare preRegister: PreRegisterService;

  /**
   * Internal property to make `currentRole` reactive; may not always represent
   * the actual ID of the current role (may be undefined)
   */
  get currentRoleId() {
    return localStorage.getItem('role') ?? undefined;
  }

  /**
   * The user object of the currently logged-in user, or an empty user object if
   * the user has not yet been loaded into the session context.
   */
  @tracked user!: UserModel;

  /**
   * The company associated with the current session context.
   */
  @tracked company?: Company;

  /**
   * An array of role objects of the currently logged-in user.
   *
   * NOTE: This is not actually the roles of the current user, but rather a
   *       filtered view. Research why this is the case. [twl 2.Aug.23]
   */
  roles: RoleViewModel[] = trackedArray([]);

  @tracked actualCurrentRelationshipType?: string;

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);
    this.user = new UserModel();
    this.actualCurrentRelationshipType = this.currentRoleId;
  }

  /**
   * Whether the authenticated user and relationships have been loaded into the session context.
   */
  get isLoaded() {
    try {
      return !!this.user.id;
    } catch {
      return false;
    }
  }

  /**
   * Whether the user has been authenticated and has a current role.
   */
  get isFullyAuthenticated() {
    return !!(this.session.isAuthenticated && this.currentRole);
  }

  /**
   * The current role for the user, or `undefined` if none exists.
   * Auto-selects the first role if multiple exist and none has been explicitly selected.
   */
  get currentRole() {
    if (this.roles.length === 0) return undefined;
    if (this.roles.length === 1) return this.roles[0];

    // If a role was previously selected (from localStorage), use it
    const savedRole = this.roles.find((role) => role.relationshipType === this.currentRoleId);
    if (savedRole) return savedRole;

    // Otherwise auto-select first role and persist it
    const firstRole = this.roles[0];
    if (firstRole) {
      localStorage.setItem('role', firstRole.relationshipType);
    }
    return firstRole;
  }

  /**
   * The current employee for the user, or an empty employee object if none
   * exists.
   */
  @cached
  get currentEmployee(): EmployeeModel {
    if (!this.user?.id || !this.user.employees?.length) {
      return this.store.createRecord<EmployeeModel>('employee', {});
    }
    return (
      this.user.employees.find(
        (employee: EmployeeModel) => employee.company.id === this.currentRole?.company?.id
      ) || this.store.createRecord<EmployeeModel>('employee', {})
    );
  }

  @cached
  get person() {
    return this.user.person || this.store.createRecord<PersonModel>('person', {});
  }

  /**
   * Gets the subdomain from the current hostname.
   * Uses shared hostname utility for consistency across services.
   */
  get subdomain() {
    return getSubdomain();
  }

  /**
   * Gets the company logo URL.
   * For non-localhost environments, uses subdomain to construct logo path.
   * For localhost, uses the first employee's company subdomain.
   */
  get logo() {
    if (isLocalhost()) {
      // In localhost, get subdomain from first employee's company setting
      const firstEmployee = this.user.employees?.[0];
      const companySubdomain = firstEmployee?.company?.companySetting?.subDomain;
      return getPartnerLogoUrl(companySubdomain, ENV.tioEnvironment);
    }

    // For non-localhost, use the subdomain from the URL
    return getPartnerLogoUrl(this.subdomain, ENV.tioEnvironment);
  }

  /**
   * Whether the user is viewing the app as an employee.
   */
  get isEmployeeView() {
    return this.currentRole?.relationshipType === 'PARTICIPANT' || this.isFamilyMember;
  }

  /**
   * Whether the user is viewing the app as a partner admin.
   */
  get isAdminView() {
    if (this.isTasRole) {
      return true;
    }

    if (!this.currentRole?.relationshipType) {
      return false;
    }

    return [
      'ACCOUNT_OWNER',
      'EMPLOYEE_ADMIN',
      'PSLF_ADMIN',
      'TIO_ACCOUNT_MANAGER',
      'TIO_ADMIN',
    ].includes(this.currentRole?.relationshipType);
  }

  get isTasRole() {
    if (!this.currentRole?.relationshipType) {
      return false;
    }

    return (
      this.currentRole.relationshipType.startsWith('TAS.Approver') ||
      this.currentRole.relationshipType === 'TAS.Admin.SUPERUSER'
    );
  }

  get isTasTioApprover() {
    if (!this.currentRole?.relationshipType) {
      return false;
    }

    return this.currentRole.relationshipType === 'TAS.Approver.TIO_APPROVER';
  }

  get canEditTasApplication() {
    if (!this.currentRole?.relationshipType) {
      return false;
    }
    const approvedRoles = [
      'TAS.Admin.PROGRAM_ADMIN',
      'TAS.Admin.SUPERUSER',
      'TAS.Approver.ADMIN_APPROVER',
      'TAS.Approver.APPROVER',
      'TAS.Approver.CUSTOM_APPROVER',
      'TAS.Approver.FINANCE_APPROVER',
      'TAS.Approver.HYPERVISOR',
      'TAS.Approver.PPP_FINANCE_APPROVER',
      'TAS.Approver.TAP_FINANCE_APPROVER',
      'TAS.Approver.TIO_APPROVER',
      'TAS.Approver.SUPERVISOR',
      'TIO_ACCOUNT_MANAGER',
      'TIO_ADMIN',
      'EMPLOYEE_ADMIN',
      'ACCOUNT_OWNER',
    ];
    return approvedRoles.includes(this.currentRole.relationshipType);
  }

  get isPslfAdmin() {
    return this.currentRole?.relationshipType === 'PSLF_ADMIN';
  }

  get isPslfGroupApprover() {
    const relationships = this.currentEmployee.jurisdictiveRelationships;
    return relationships.some((jr) => jr.relationshipType === 'PSLF_GROUP_APPROVER');
  }

  get isFamilyMember() {
    return this.currentRole?.role === 'FAMILY_MEMBER';
  }

  get isEmailProviderLogin() {
    const userLogin = this.user.logins?.[0];
    return userLogin?.provider === 'EMAIL';
  }

  // get showWellness() {
  //   const enabled = this.features.isEnabled('wellnessInsights') && this.partner.hasWellness;
  //   const notD5Train = this.sessionContext.user?.wellnessTrain?.wellnessTrain !== 'D-5';
  //   return enabled && notD5Train;
  // }

  get isOtherProviderLogin() {
    if (!this.user.id) {
      return false;
    }
    return !this.isEmailProviderLogin;
  }

  get hasAcceptedAppTerms() {
    return !!(
      this.user.usageAgreement &&
      this.user.privacyAgreement &&
      this.hasAcceptedAllCustomRegisterTerms
    );
  }

  get needsToAcceptAppTerms() {
    return this.user && !this.hasAcceptedAppTerms;
  }

  @cached
  get hasAcceptedAllCustomRegisterTerms() {
    const customRegisterAgreementTermsIds = new Set(
      // @ts-expect-error: seems like relation is typed wrong
      (this.user.customRegisterAgreements || []).map((a: AgreementModel) => a.condition?.id)
    );
    const customRegisterTermIds = (this.preRegister.customTerms || []).map((t) => t.id);

    if (customRegisterAgreementTermsIds.size < customRegisterTermIds.length) {
      return false;
    }
    return customRegisterTermIds.every((termId) => {
      return customRegisterAgreementTermsIds.has(termId);
    });
  }

  get isParticipantOnly() {
    return this.roles?.length === 1 && this.roles[0]?.relationshipType === 'PARTICIPANT';
  }

  /**
   * Loads the user data for the current authenticated user.
   */
  load = async () => {
    const data = await fetch(ENV.apiHost + '/me?include=' + meIncludes.join(','), {
      headers: {
        'x-api-key': ENV.apiKey,
        'tio-auth-token': this.session.data.authenticated?.access_token || '',
        Accept: 'application/vnd.api+json',
        'Time-Zone': timeZone(),
        'X-Request-Id': generateRequestId(),
      },
    });

    if ([401, 403, 404].includes(data.status)) {
      this.logout();

      throw new Error(`Unauthorized status loading user data: ${data.status}`);
    }

    const parsed = await data.json();
    // @ts-expect-error - Ember Data pushPayload expects 2 arguments after rollback
    this.store.pushPayload(parsed);
    // @ts-expect-error: not sure
    this.user = this.store.peekRecord<UserModel>('user', parsed.data.id);
    this.roles = trackedArray([...this.user.uniqueRolesByRelationshipType]);
    if (this.isFamilyMember) {
      const chosenCompany = parsed.included?.find(
        (inc: { type: string; id: string }) => inc.type === 'company'
      );
      if (chosenCompany) {
        this.company = this.store.peekRecord<Company>('company', chosenCompany.id) || undefined;
      }
    }
  };

  /**
   * Returns the employee IDs fo the current user.
   *
   * HACK: We are struggling with hard-to-reproduce timing bugs that are causing
   *       `sessionContext.user.person.employees` to evaluate as empty
   *       unexpectedly.
   */
  getEmployeeIdsAsync = async () => {
    let employees = this.user?.person?.employees || [];

    if (!employees.length) {
      // @ts-expect-error: need to detach models from types
      const userWithEmployees = await this.store.findRecord<UserModel>('user', this.user.id, {
        include: 'person.employees',
        reload: true,
      });
      employees = userWithEmployees.person?.employees || [];
    }
    return employees.map((e) => e.id);
  };

  /**
   * Sets the current role for the authenticated user.
   *
   * @param {RoleView} role - A user role
   */
  setCurrentRole = (role: RoleViewModel) => {
    this.actualCurrentRelationshipType = role.relationshipType;
    localStorage.setItem('role', role.relationshipType);
    if (this.isEmployeeView) {
      this.router.transitionTo('authenticated.dashboard');
    } else {
      this.router.transitionTo('authenticated.admin');
    }

    try {
      setUserProperty('role', this.currentRole?.role);
      setUserProperty('relationshipType', this.currentRole?.relationshipType);
    } catch (e) {
      console.error('Error updating DataDog user current role', e);
    }
  };

  /**
   * Destroys the authenticated session and logs out the current user.
   */
  logout = async () => {
    try {
      this.zendesk.clearUser();
    } catch (e) {
      console.error('[service:session-context] Could not clear zendesk user', e);
    }

    try {
      clearUser();
    } catch (e) {
      console.error('[service:session-context] Could not clear datadog user', e);
    }

    // This must be last, since this causes a redirect once it is executed
    try {
      await this.session.invalidate();
    } catch (e) {
      console.error('[service:session-context] Could not clear session context', e);
    }

    localStorage.removeItem('role');
    sessionStorage.clear();

    try {
      this.roles = [];
      this.user = new UserModel();
    } catch (e) {
      console.error('[service:session-context] Could not clear user and roles', e);
    }
  };
}

export default SessionContextService;
